import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDatabase as Database,
  FiSearch as Search,
  FiPlus as Plus,
  FiRefreshCw as RefreshCw,
  FiClock as Clock,
  FiChevronRight as ChevronRight,
  FiAlertTriangle as AlertTriangle,
  FiCheckCircle as CheckCircle,
  FiCloud as Cloud,
  FiHash as Hash,
  FiTrash2 as Trash2,
  FiLoader as Loader,
  FiSquare as Square,
  FiCheckSquare as CheckSquare
} from 'react-icons/fi';
import { listVectorExports, deleteVectorExport, syncVectorExport } from '../../../../../services/vectorExportsApi';
import { assignVectorExportProject, bulkAssignVectorExportProject, assignVectorExportWorkload, bulkAssignVectorExportWorkload } from '../../../../../services/vectorExportsApi';
import { bulkDeleteVectorExports } from '../../../../../services/aiOperationsApi';
import { listDatasets } from '../../../../../services/datasetsApi';
import { apiCall } from '../../../../../services/api';
import CreateVectorExportModal, { Dataset, AwsAccount } from './CreateVectorExportModal';
import { ConfirmDialog } from '../../../../../components/ui';
import ProjectAssignmentSelect from '../../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../../components/WorkloadAssignmentSelect';
import { useProject } from '../../../../../contexts/ProjectContext';

interface VectorExport {
  id: string;
  display_name: string;
  project_id?: string | null;
  dataset_id: string;
  aws_account_id: string;
  status: string;
  vector_count?: number;
  auto_sync?: boolean;
  vector_bucket_name?: string;
  last_sync_at?: string;
  created_at?: string;
  region?: string;
  workload_id?: string | null;
  workload_name?: string;
}

interface DeleteDialog {
  isOpen: boolean;
  export: VectorExport | null;
  step: 'aws' | 'confirm';
}

interface Props {
  onExportClick?: (id: string) => void;
}

type StatusKey = 'pending' | 'in_progress' | 'completed' | 'failed' | 'syncing';

interface StatusConfig {
  bg: string;
  text: string;
  icon: React.ComponentType<{ className?: string }>;
}

const VectorExportsContent: React.FC<Props> = ({ onExportClick }) => {
  const navigate = useNavigate();
  const { projects } = useProject();
  const [exports, setExports] = useState<VectorExport[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [awsAccounts, setAwsAccounts] = useState<AwsAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialog>({ isOpen: false, export: null, step: 'aws' });
  const [deleteAwsResources, setDeleteAwsResources] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [exportsData, datasetsData, accountsData] = await Promise.all([
        listVectorExports({ limit: 100 }),
        listDatasets({ limit: 100 }),
        apiCall('/aws/accounts')
      ]);

      setExports((exportsData as { data?: VectorExport[] }).data ?? (exportsData as VectorExport[]) ?? []);
      setDatasets((datasetsData as { data?: Dataset[] }).data ?? (datasetsData as Dataset[]) ?? []);
      setAwsAccounts((accountsData as AwsAccount[]) ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load vector exports';
      console.error('Error loading vector exports:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (e: React.MouseEvent, exportItem: VectorExport): Promise<void> => {
    e.stopPropagation();
    if (syncingId) return;

    setSyncingId(exportItem.id);
    try {
      await syncVectorExport(exportItem.id);
      void loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sync export';
      console.error('Error syncing export:', err);
      alert(message);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, exportItem: VectorExport): void => {
    e.stopPropagation();
    setDeleteDialog({ isOpen: true, export: exportItem, step: 'aws' });
    setDeleteAwsResources(false);
  };

  const handleAwsResourcesChoice = (deleteAws: boolean): void => {
    setDeleteAwsResources(deleteAws);
    setDeleteDialog(prev => ({ ...prev, step: 'confirm' }));
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteDialog.export) return;

    setIsDeleting(true);
    try {
      await deleteVectorExport(deleteDialog.export.id, deleteAwsResources);
      setDeleteDialog({ isOpen: false, export: null, step: 'aws' });
      void loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete export';
      console.error('Error deleting export:', err);
      alert(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialog({ isOpen: false, export: null, step: 'aws' });
    setDeleteAwsResources(false);
  };

  const handleExportCreated = (): void => {
    setShowCreateModal(false);
    void loadData();
  };

  const filteredExports = exports.filter(exp => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      exp.display_name?.toLowerCase().includes(searchLower) ||
      exp.vector_bucket_name?.toLowerCase().includes(searchLower) ||
      exp.id?.toLowerCase().includes(searchLower)
    );
  });

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filteredExports.length
        ? new Set()
        : new Set(filteredExports.map((e) => e.id))
    );
  }, [filteredExports]);

  const handleBulkDelete = async (): Promise<void> => {
    if (selectedIds.size === 0) return;
    if (!globalThis.confirm(`Delete ${selectedIds.size} selected export(s)?`)) return;
    try {
      await bulkDeleteVectorExports(Array.from(selectedIds));
      setSelectedIds(new Set());
      void loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete selected exports';
      console.error('Error bulk deleting exports:', err);
      alert(message);
    }
  };

  const formatTimestamp = (timestamp: string | undefined): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString();
  };

  const getStatusBadge = (status: string): React.ReactNode => {
    const statusConfig: Record<StatusKey, StatusConfig> = {
      pending: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300', icon: Clock },
      in_progress: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-800 dark:text-blue-300', icon: Loader },
      completed: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-300', icon: CheckCircle },
      failed: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300', icon: AlertTriangle },
      syncing: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-300', icon: RefreshCw }
    };

    const config = statusConfig[status as StatusKey] ?? statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${config.bg} ${config.text}`}>
        <Icon className={`w-3 h-3 ${['in_progress', 'syncing'].includes(status) ? 'animate-spin' : ''}`} />
        {status}
      </span>
    );
  };

  const getDatasetName = (datasetId: string): string => {
    const dataset = datasets.find(d => d.id === datasetId);
    return dataset?.name ?? 'Unknown Dataset';
  };

  const getAccountInfo = (awsAccountId: string): string => {
    const account = awsAccounts.find(a => a.id === awsAccountId);
    return account?.account_id ?? 'Unknown';
  };

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find((project) => project.id === projectId)?.name ?? null;
  };

  const totalExports = exports.length;
  const totalVectors = exports.reduce((sum, e) => sum + (e.vector_count ?? 0), 0);
  const completedExports = exports.filter(e => e.status === 'completed').length;

  const formatNumber = (num: number | null | undefined): string => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  let newExportButtonTitle: string;
  if (datasets.length === 0) {
    newExportButtonTitle = 'Create a dataset first';
  } else if (awsAccounts.length === 0) {
    newExportButtonTitle = 'Configure AWS account first';
  } else {
    newExportButtonTitle = '';
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Exports</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalExports)}
              </p>
            </div>
            <Cloud className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Vectors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalVectors)}
              </p>
            </div>
            <Hash className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(completedExports)}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={toggleSelectAll}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={selectedIds.size === filteredExports.length && filteredExports.length > 0 ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === filteredExports.length && filteredExports.length > 0 ? (
              <CheckSquare className="w-5 h-5" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search exports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <button
            onClick={() => void loadData()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={datasets.length === 0 || awsAccounts.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={newExportButtonTitle}
          >
            <Plus className="w-4 h-4" />
            New Export
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {selectedIds.size} export{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              onAssign={async (projectId) => {
                await bulkAssignVectorExportProject(Array.from(selectedIds), projectId);
                setExports((prev) => prev.map((exp) => (
                  selectedIds.has(exp.id) ? { ...exp, project_id: projectId } : exp
                )));
                setSelectedIds(new Set());
              }}
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                await bulkAssignVectorExportWorkload(Array.from(selectedIds), workloadId);
                setSelectedIds(new Set());
                void loadData();
              }}
            />
            <button
              onClick={() => void handleBulkDelete()}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Prerequisites Warning */}
      {(datasets.length === 0 || awsAccounts.length === 0) && (
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">Prerequisites Required</h4>
              <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                {datasets.length === 0 && (
                  <li>
                    - Create at least one dataset with test cases in the{' '}
                    <button
                      onClick={() => navigate('/ai-operations/registry?tab=datasets')}
                      className="underline hover:no-underline"
                    >
                      Registry
                    </button>
                  </li>
                )}
                {awsAccounts.length === 0 && (
                  <li>
                    - Configure an AWS account in the{' '}
                    <button
                      onClick={() => navigate('/hub/aws-environments')}
                      className="underline hover:no-underline"
                    >
                      Hub &gt; AWS Environments
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Exports List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {filteredExports.length === 0 ? (
          <div className="p-12">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cloud className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Export Your First Dataset to S3 Vectors
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Export your datasets to AWS S3 Vectors for semantic search and RAG applications.
                Vectors are stored in your own AWS account.
              </p>

              {/* Quick Start Guide */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">How it Works</h4>
                <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">1</span>
                    <span>Select a dataset with your content (FAQs, docs, test cases)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">2</span>
                    <span>Choose your AWS account (vectors are stored in your account)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">3</span>
                    <span>We generate embeddings and create the vector index</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">4</span>
                    <span>Use the vectors in your RAG apps or Bedrock Knowledge Bases</span>
                  </li>
                </ol>
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                disabled={datasets.length === 0 || awsAccounts.length === 0}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
                Create First Export
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredExports.map((exp) => (
              <div
                key={exp.id}
                onClick={() => onExportClick ? onExportClick(exp.id) : navigate(`/ai-operations/vector-exports/${exp.id}`)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onExportClick ? onExportClick(exp.id) : navigate(`/ai-operations/vector-exports/${exp.id}`); }} role="button" tabIndex={0}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(exp.id); }}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {selectedIds.has(exp.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {exp.display_name}
                        </span>
                        {getStatusBadge(exp.status)}
                        {getProjectName(exp.project_id) && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                            {getProjectName(exp.project_id)}
                          </span>
                        )}
                        {exp.workload_name && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-300 rounded">
                            {exp.workload_name}
                          </span>
                        )}
                        {exp.auto_sync && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                            Auto-sync
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Database className="w-3 h-3" />
                          {getDatasetName(exp.dataset_id)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Cloud className="w-3 h-3" />
                          AWS {getAccountInfo(exp.aws_account_id)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {formatNumber(exp.vector_count)} vectors
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(exp.last_sync_at ?? exp.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <ProjectAssignmentSelect
                        currentProjectId={exp.project_id}
                        onAssign={async (projectId) => {
                          await assignVectorExportProject(exp.id, projectId);
                          setExports((prev) => prev.map((item) => (
                            item.id === exp.id ? { ...item, project_id: projectId } : item
                          )));
                        }}
                      />
                    </div>
                    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <WorkloadAssignmentSelect
                        currentWorkloadId={exp.workload_id}
                        onAssign={async (workloadId) => {
                          await assignVectorExportWorkload(exp.id, workloadId);
                          void loadData();
                        }}
                      />
                    </div>
                    <button
                      onClick={(e) => void handleSync(e, exp)}
                      disabled={syncingId === exp.id || ['in_progress', 'syncing'].includes(exp.status)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Sync with dataset"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncingId === exp.id ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(e, exp)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete export"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Export Modal */}
      {showCreateModal && (
        <CreateVectorExportModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleExportCreated}
          datasets={datasets}
          awsAccounts={awsAccounts}
        />
      )}

      {/* Delete AWS Resources Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen && deleteDialog.step === 'aws'}
        onClose={handleDeleteCancel}
        onConfirm={() => handleAwsResourcesChoice(true)}
        title="Delete AWS Resources?"
        message={
          <div>
            <p className="mb-3">
              Do you want to also delete the S3 Vectors bucket and index in your AWS account?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This will permanently delete the vectors from AWS. Choose &ldquo;Keep AWS Resources&rdquo; to only remove the export from Aivilo.
            </p>
          </div>
        }
        confirmText="Delete AWS Resources"
        cancelText="Keep AWS Resources"
        variant="danger"
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen && deleteDialog.step === 'confirm'}
        onClose={handleDeleteCancel}
        onConfirm={() => void handleDeleteConfirm()}
        title="Confirm Delete"
        message={
          <div>
            <p>
              Are you sure you want to delete &ldquo;<strong>{deleteDialog.export?.display_name}</strong>&rdquo;?
            </p>
            {deleteAwsResources && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                This will also delete the S3 Vectors bucket and index from AWS.
              </p>
            )}
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VectorExportsContent;
